import { Pipe, PipeTransform } from '@angular/core';
import { ShowPerformance } from '../models/showperformance.model';
import { CrewRole } from './crew-role';
import { User } from '../models/user.model';
import { ShowreportCrewAssignment } from './showreport/showreport.model';

@Pipe({
    standalone: true,
    name: 'getUserFullNameFromPerformanceWithRole'
})
export class GetUserFullNameFromPerformanceWithRolePipe implements PipeTransform {

    transform(performance: ShowPerformance, role: CrewRole): string {

        if (!performance) {
            return null;
        }

        if (role === CrewRole.stagemanager) {
            return this.getStagemanagerName(performance);
        }

        if (role === CrewRole.toneelmeester) {
            return this.getToneelmeesterName(performance);
        }

        return 'Onbekend';
    }

    /**
     * Resolves the toneelmeester to display for a performance card.
     *
     * Priority:
     *   1. Log artifact (most authoritative once filled)
     *   2. Showreport artifact — for dynamic shows we read from
     *      `crewAssignments` only; for legacy shows we read the flat
     *      `toneelmeester` field. We do NOT cross-fall-back, because a
     *      dynamic show's legacy fields can carry stale defaults that
     *      have nothing to do with the actual recorded crew.
     *   3. Scheduled toneelmeester (only when no artifact exists yet)
     *
     * Once an artifact exists, its recorded value is ground truth — even
     * when blank. Falling back to the schedule when the user has actively
     * filled in a form would silently substitute the planned crew member
     * for one the stagemanager chose to leave empty. Scheduled-crew
     * fallback for dynamic showreports isn't wired up yet, so a missing
     * dynamic assignment renders as `-`.
     */
    public getToneelmeesterName(performance: ShowPerformance): string {
        if (performance?.log) {
            return this.userNameFromField(performance, 'log', CrewRole.toneelmeester) || '-';
        }
        if (performance?.showreport) {
            return this.userNameFromShowreport(performance, CrewRole.toneelmeester) || '-';
        }
        return this.userFullName(performance?.scheduledToneelmeester) || '-';
    }

    /**
     * Resolves the stagemanager to display for a performance card.
     *
     * Priority:
     *   1. Showreport artifact (dynamic: `crewAssignments`; legacy: flat
     *      `stagemanager` field — no cross-fall-back, see toneelmeester)
     *   2. Scheduled stagemanager (only when no showreport exists yet)
     */
    public getStagemanagerName(performance: ShowPerformance): string {
        if (performance?.showreport) {
            return this.userNameFromShowreport(performance, CrewRole.stagemanager) || '-';
        }
        return this.userFullName(performance?.scheduledStagemanager) || '-';
    }

    /**
     * Reads the user assigned to a role on a showreport. The lookup
     * source depends on the show: dynamic-template shows store crew in
     * {@link Showreport.crewAssignments}; legacy shows store it on the
     * flat field matching the role name.
     */
    private userNameFromShowreport(performance: ShowPerformance, role: CrewRole): string | null {
        const showreport = performance?.showreport;
        if (!showreport) {
            return null;
        }
        if (this.usesLegacyShowreport(performance)) {
            const legacyUser = (showreport as unknown as Record<string, User | undefined>)?.[role.toLowerCase()];
            return this.userFullName(legacyUser);
        }
        return this.userNameFromCrewAssignments(showreport.crewAssignments, role);
    }

    /**
     * True when the performance's show uses the legacy static showreport
     * (and so stores crew on the flat fields rather than in
     * `crewAssignments`).
     */
    private usesLegacyShowreport(performance: ShowPerformance): boolean {
        return performance?.show?.useLegacyShowreport === true;
    }

    /**
     * Looks up a role in the dynamic-template `crewAssignments` array.
     * Matches on `roleTypeName` case-insensitively against the CrewRole
     * value — works for the standard "Stagemanager" / "Toneelmeester"
     * display names that most shows ship with.
     */
    private userNameFromCrewAssignments(
        assignments: ShowreportCrewAssignment[] | undefined,
        role: CrewRole
    ): string | null {
        if (!Array.isArray(assignments) || assignments.length === 0) {
            return null;
        }
        const target = role.toLowerCase();
        const match = assignments.find(a => a?.roleTypeName?.toLowerCase() === target);
        const fullName = match?.userFullName?.trim();
        return fullName && fullName.length > 0 ? fullName : null;
    }

    /** Reads `performance[roleField][roleType]` and formats the user. */
    private userNameFromField(performance: ShowPerformance, roleField: string, roleType: CrewRole): string | null {
        const user: User | undefined = performance?.[roleField]?.[roleType.toLowerCase()];
        return this.userFullName(user);
    }

    /**
     * Returns the user's display name, or null for an absent or
     * default-instance User. The legacy `Showreport` model defaults its
     * crew fields to `new User()`, so a missing assignment doesn't show
     * up as `null` — it shows up as a User with id 0 and empty names.
     * Using `id` as the realness check sidesteps that.
     */
    private userFullName(user: User | null | undefined): string | null {
        if (!user?.id) {
            return null;
        }
        const name = typeof user.getFullName === 'function'
            ? user.getFullName()
            : `${user.firstName ?? ''} ${user.lastName ?? ''}`;
        const trimmed = name?.trim();
        return trimmed && trimmed.length > 0 ? trimmed : null;
    }
}
